import Dexie, { type Table } from 'dexie'
import type { Inscricao } from '../modules/inscricoes/types'
import type { Atirador } from '../modules/atiradores/types'

export interface SyncQueueItem {
  id?: number
  table: string
  action: 'create' | 'update' | 'delete'
  payload: Record<string, unknown>
  endpoint: string
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH'
  retryCount: number
  createdAt: Date
  errorMessage?: string
}

export interface CachedCampeonato {
  id: number
  data: unknown
  updatedAt: Date
}

export interface CachedEtapa {
  id: number
  idcampeonato: number
  data: unknown
  updatedAt: Date
}

export interface CachedModalidade {
  id: number
  data: unknown
  updatedAt: Date
}

export interface OfflineAtirador extends Atirador {
  syncStatus: 'pending' | 'synced' | 'error'
  tempId?: string
}

export interface OfflineInscricao extends Inscricao {
  syncStatus: 'pending' | 'synced' | 'error'
  tempId?: string
}

class OfflineDatabase extends Dexie {
  syncQueue!: Table<SyncQueueItem, number>
  atiradores!: Table<OfflineAtirador, number>
  inscricoes!: Table<OfflineInscricao, number>
  campeonatos!: Table<CachedCampeonato, number>
  etapas!: Table<CachedEtapa, number>
  modalidades!: Table<CachedModalidade, number>
  apiCache!: Table<ApiCacheEntry, string>

  constructor() {
    super('CampeonatosTiroDB')
    this.version(2).stores({
      syncQueue: '++id, table, action, createdAt',
      atiradores: '++idatirador, tempId, syncStatus, nome, cpf',
      inscricoes: '++idinscricao, tempId, syncStatus, idatirador, status',
      campeonatos: '++id, updatedAt',
      etapas: '++id, idcampeonato, updatedAt',
      modalidades: '++id, updatedAt',
      apiCache: 'key, updatedAt',
    })
  }
}

export const db = new OfflineDatabase()

// ── Helpers de fila de sincronização ────────────────────────────────────────

export async function addToSyncQueue(
  item: Omit<SyncQueueItem, 'id' | 'retryCount' | 'createdAt'>,
): Promise<void> {
  await db.syncQueue.add({
    ...item,
    retryCount: 0,
    createdAt: new Date(),
  })
}

export async function getSyncQueue(): Promise<SyncQueueItem[]> {
  return db.syncQueue.orderBy('createdAt').toArray()
}

export async function removeFromSyncQueue(id: number): Promise<void> {
  await db.syncQueue.delete(id)
}

export async function updateSyncQueueError(id: number, error: string): Promise<void> {
  const item = await db.syncQueue.get(id)
  if (item) {
    await db.syncQueue.update(id, {
      retryCount: item.retryCount + 1,
      errorMessage: error,
    })
  }
}

export async function clearSyncQueue(): Promise<void> {
  await db.syncQueue.clear()
}

// Itens que ja bateram o limite de tentativas (ver MAX_SYNC_RETRIES em
// useOfflineSync.ts) nunca mais vao sincronizar sozinhos — nao contam como
// "pendente" (senao o badge/contador fica preso pra sempre e todo evento de
// "voltou a internet" tenta sincronizar de novo à toa). Eles continuam
// visiveis na Fila Offline pra remocao manual.
export const MAX_SYNC_RETRIES = 5

export async function getPendingSyncCount(): Promise<number> {
  return db.syncQueue.filter(item => item.retryCount < MAX_SYNC_RETRIES).count()
}

// ── Cache de dados mestre ────────────────────────────────────────────────────

export async function cacheCampeonatos(campeonatos: unknown[]): Promise<void> {
  await db.campeonatos.clear()
  await db.campeonatos.bulkAdd(
    (campeonatos as Array<{ idcampeonato: number }>).map(c => ({
      id: c.idcampeonato,
      data: c,
      updatedAt: new Date(),
    })),
  )
}

export async function getCachedCampeonatos(): Promise<unknown[]> {
  const rows = await db.campeonatos.toArray()
  return rows.map(r => r.data)
}

export async function cacheEtapas(idcampeonato: number, etapas: unknown[]): Promise<void> {
  await db.etapas.where('idcampeonato').equals(idcampeonato).delete()
  await db.etapas.bulkAdd(
    (etapas as Array<{ idetapa: number }>).map(e => ({
      id: e.idetapa,
      idcampeonato,
      data: e,
      updatedAt: new Date(),
    })),
  )
}

export async function getCachedEtapas(idcampeonato: number): Promise<unknown[]> {
  const rows = await db.etapas.where('idcampeonato').equals(idcampeonato).toArray()
  return rows.map(r => r.data)
}

export async function cacheModalidades(modalidades: unknown[]): Promise<void> {
  await db.modalidades.clear()
  await db.modalidades.bulkAdd(
    (modalidades as Array<{ idmodalidade: number }>).map(m => ({
      id: m.idmodalidade,
      data: m,
      updatedAt: new Date(),
    })),
  )
}

export async function getCachedModalidades(): Promise<unknown[]> {
  const rows = await db.modalidades.toArray()
  return rows.map(r => r.data)
}

// ── Cache generico de API ───────────────────────────────────────────────────

export interface ApiCacheEntry {
  key: string
  data: unknown
  updatedAt: Date
}

export async function cacheApiResponse(key: string, data: unknown): Promise<void> {
  await db.apiCache.put({ key, data, updatedAt: new Date() })
}

export async function getCachedApiResponse(key: string): Promise<unknown | null> {
  const entry = await db.apiCache.get(key)
  return entry?.data ?? null
}

export async function clearApiCache(): Promise<void> {
  await db.apiCache.clear()
}

export async function addOfflineAtirador(atirador: Omit<OfflineAtirador, 'idatirador' | 'syncStatus'>): Promise<number> {
  return db.atiradores.add({
    ...atirador,
    syncStatus: 'pending',
    data_cadastro: new Date().toISOString(),
  } as OfflineAtirador)
}

export async function getOfflineAtiradores(): Promise<OfflineAtirador[]> {
  return db.atiradores.toArray()
}

export async function updateOfflineAtirador(id: number, changes: Partial<OfflineAtirador>): Promise<void> {
  await db.atiradores.update(id, changes)
}

// ── Inscricoes offline ──────────────────────────────────────────────────────

export async function addOfflineInscricao(inscricao: Omit<OfflineInscricao, 'idinscricao' | 'syncStatus'>): Promise<number> {
  return db.inscricoes.add({
    ...inscricao,
    syncStatus: 'pending',
    data_cadastro: new Date().toISOString(),
  } as OfflineInscricao)
}

export async function getOfflineInscricoes(): Promise<OfflineInscricao[]> {
  return db.inscricoes.toArray()
}

export async function updateOfflineInscricao(id: number, changes: Partial<OfflineInscricao>): Promise<void> {
  await db.inscricoes.update(id, changes)
}
